import { cva, VariantProps } from "cva";
import { XIcon } from "lucide-react";
import React from "react";
import {
  Dialog,
  DialogProps,
  ModalOverlay,
  Button as RACButton,
  Modal as RACModal,
} from "react-aria-components";
import { twMerge } from "tailwind-merge";

const modalOverlayVariants = cva({
  base: [
    "fixed inset-0 flex items-center justify-center bg-black/20 backdrop-blur-sm p-4",
  ],
  variants: {
    animation: {
      fade: [
        "entering:animate-in entering:fade-in entering:duration-200",
        "exiting:animate-out exiting:fade-out exiting:duration-200",
      ],
      scale: [
        "entering:animate-in entering:fade-in entering:zoom-in-95 entering:duration-200",
        "exiting:animate-out exiting:fade-out exiting:zoom-out-95 exiting:duration-200",
      ],
    },
  },
  defaultVariants: {
    animation: "fade",
  },
});

const modalVariants = cva({
  base: [
    "relative bg-theme-background rounded-lg overflow-auto outline-none w-full",
  ],
  variants: {
    size: {
      sm: "max-w-sm",
      md: "max-w-md",
      lg: "max-w-lg",
      xl: "max-w-xl",
      "2xl": "max-w-2xl",
      "3xl": "max-w-3xl",
      "4xl": "max-w-4xl",
      "5xl": "max-w-5xl",
      full: "max-w-[95vw] max-h-[95vh]",
    },
    padding: {
      none: null,
      sm: "p-4",
      md: "p-6",
      lg: "p-8",
      xl: "p-10",
    },
    intent: {
      default: null,
    },
  },

  defaultVariants: {
    size: "md",
    padding: "lg",
    intent: "default",
  },
});

type StyledModalProps = VariantProps<typeof modalVariants> &
  VariantProps<typeof modalOverlayVariants> & {
    children: DialogProps["children"];
    isOpen?: boolean;
    onOpenChange?: (isOpen: boolean) => void;
    isDismissable?: boolean;
    className?: string;
    overlayClassName?: string;
    icon?: React.ReactNode;
    closeButtonClassName?: string;
  } & Pick<DialogProps, "aria-label">;

export const Modal = React.forwardRef<HTMLDivElement, StyledModalProps>(
  (
    {
      children,
      size,
      padding,
      intent,
      animation,
      className,
      overlayClassName,
      isOpen,
      onOpenChange,
      isDismissable = true,
      icon,
      closeButtonClassName,
      "aria-label": ariaLabel,
      ...props
    },
    ref,
  ) => {
    return (
      <ModalOverlay
        className={twMerge(
          modalOverlayVariants({ animation }),
          overlayClassName,
        )}
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        isDismissable={isDismissable}
      >
        <RACModal
          ref={ref}
          className={twMerge(
            modalVariants({ size, padding, intent }),
            className,
          )}
          {...props}
        >
          <Dialog
            className="relative flex w-full flex-col items-center p-5"
            aria-label={ariaLabel}
          >
            {(renderProps) => (
              <>
                {icon && (
                  <div className="relative mb-6 w-fit">
                    <div className="bg-theme-gradient-from/20 border-theme-gradient-from/20 absolute inset-0 rounded-full border-2" />
                    <div className="relative z-10 p-4">{icon}</div>
                  </div>
                )}

                {isDismissable && (
                  <RACButton
                    slot="close"
                    className={twMerge(
                      "absolute top-0 right-0 -mt-0.5 cursor-pointer",
                      closeButtonClassName,
                    )}
                  >
                    <XIcon className="text-theme-on-glass size-5 cursor-pointer" />
                  </RACButton>
                )}

                {typeof children === "function"
                  ? children(renderProps)
                  : children}
              </>
            )}
          </Dialog>
        </RACModal>
      </ModalOverlay>
    );
  },
);
